import React, { ReactNode, useContext, useRef, useMemo, useEffect } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./logo_color.png";
import IconExpand from "@/icons/IconExpand";
import IconCollapse from "@/icons/IconCollapse";
import useMenuItems from "@/hooks/useMenuItems";
import { MenuItem } from "@/types";
import MainLink from "./MainLink";
import ProfileButton from "./ProfileButton";
import { ProfileContext, StatusContext, useLocalStorage } from "@velaro/velaro-component-lib";
import useSite from "@/hooks/useSite";
import ExpandableMenuLink from "./ExpandableMenuLink";
const LogoWhite = "/img/Logo_White.svg";
const messagingClientUrl = import.meta.env.VITE_MESSAGING_CLIENT_URL as string;
import { LinkExternal01 } from "@velaro/velaro-component-lib";
import DesktopProfileMenu from "./DesktopProfileMenu";
import Tooltip from "@/components/Tooltip";
import CopilotAssistant from "@/components/CopilotAssistant";
import useVersion from "@/hooks/useVersion";
import NewVersionBanner from "@/components/NewVersionBanner";

const ZOOM_LEVELS = [0.84, 0.92, 1.0, 1.09, 1.18] as const;

interface Props {
  children: ReactNode;
}

const styles = {
  subLink:
    "ml-5 p-2 v-nav-hover block rounded-lg transition duration-200 delay-100 hover:delay-100",
  subLinkActive:
    "ml-5 p-2 rounded-lg v-nav-active block transition duration-200 delay-100 hover:delay-100"
};

function SubLink({
  to,
  label,
  onVisit,
  end = false
}: {
  to: string;
  label: string;
  onVisit(): void;
  end?: boolean;
}) {
  if (to.startsWith("http")) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        className={styles.subLink}
        onClick={onVisit}
      >
        <span className="w-full text-left text-sm">{label}</span>
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        isActive ? styles.subLinkActive : styles.subLink
      }
      onClick={onVisit}
    >
      <span className="w-full text-left text-sm">{label}</span>
    </NavLink>
  );
}
export default function MainLayout(props: Props) {
  const [leftNavExpanded, setLeftNavExpanded] = React.useState(true);
  const [expandedMenus, setExpandedMenus] = React.useState<string[]>([]);
  const [toggleSidebar, setToggleSidebar] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const [showCopilot, setShowCopilot] = React.useState(false);

  const [fontStep, setFontStep] = useLocalStorage<number>("admin-font-step", 2);
  const decreaseFont = () => setFontStep(Math.max(0, fontStep - 1));
  const increaseFont = () => setFontStep(Math.min(ZOOM_LEVELS.length - 1, fontStep + 1));
  const zoomStyle = useMemo(() => ({ zoom: ZOOM_LEVELS[fontStep] }), [fontStep]);

  // Draggable FAB — position persisted in sessionStorage
  const [fabPos, setFabPos] = React.useState<{ bottom: number; right: number }>(() => {
    try {
      const saved = sessionStorage.getItem("copilot-fab-pos");
      return saved ? JSON.parse(saved) : { bottom: 24, right: 24 };
    } catch { return { bottom: 24, right: 24 }; }
  });
  const fabDragStart = useRef<{ mx: number; my: number; ob: number; or: number } | null>(null);
  const fabDragging = useRef(false);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!fabDragStart.current) return;
      // Require at least 5px of movement before treating as a drag — prevents
      // tiny mouse jitter from swallowing the click event
      const dx = e.clientX - fabDragStart.current.mx;
      const dy = e.clientY - fabDragStart.current.my;
      if (!fabDragging.current && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      fabDragging.current = true;
      const newBottom = fabDragStart.current.ob - (e.clientY - fabDragStart.current.my);
      const newRight  = fabDragStart.current.or  - (e.clientX - fabDragStart.current.mx);
      const next = {
        bottom: Math.max(8, Math.min(newBottom, window.innerHeight - 56)),
        right:  Math.max(8, Math.min(newRight,  window.innerWidth  - 56)),
      };
      setFabPos(next);
      try { sessionStorage.setItem("copilot-fab-pos", JSON.stringify(next)); } catch { /* ignore */ }
    };
    const onUp = () => { fabDragStart.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const menuItems = useMenuItems();

  // Map integration URL segments to their IntegrationLogger key names
  const INTEGRATION_KEY_MAP: Record<string, string> = {
    netsuite: "NetSuite",
    hubspot: "HubSpot",
    dynamics: "Dynamics",
    shopify: "Shopify",
    bigcommerce: "BigCommerce",
    woocommerce: "WooCommerce",
    magento: "Magento",
    square: "Square",
    salesforce: "Salesforce",
    accela: "AccelaCivic",
  };
  const integrationKeyMatch = location.pathname.match(/\/integrations\/([^/]+)/);
  const integrationKey = integrationKeyMatch
    ? INTEGRATION_KEY_MAP[integrationKeyMatch[1].toLowerCase()]
    : undefined;

  // Build a rich, human-readable description of the current screen for Moshky
  const getPageContext = (pathname: string): string => {
    const p = pathname.toLowerCase();
    const PAGE_LABELS: Array<[RegExp, string]> = [
      [/\/messaging\/workflows\/\d+/, "Workflow Editor — the user is editing a specific workflow. They can add/edit nodes (AI, Transfer, Send Message, etc.), configure the system prompt, and connect integrations."],
      [/\/messaging\/workflows/, "Workflows page — the user can create, edit, and manage AI workflows. Workflows define how the bot responds to visitors. Use [→ New Workflow](/messaging/workflows) to create one."],
      [/\/messaging\/bots\/\d+/, "Bot Profile editor — the user is editing a specific AI bot profile (persona, avatar, language, escalation settings)."],
      [/\/messaging\/bots/, "Bot Profiles page — the user can create and configure AI bot personas. Each bot has a name, avatar, and personality. Use [→ New Bot](/messaging/bots) to create one."],
      [/\/messaging\/channels\/ivr/, "IVR / Voice Channel settings — the user is configuring Velaro's AI phone system (inbound calls, Twilio number, TTS voice, transfer numbers). The Starter Wizard can create ready-to-use IVR workflows. Moshky can explain: dedicated IVR bot vs routing bot that delegates to specialists."],
      [/\/messaging\/channels\/voice/, "Voice / CTI Channel settings — the user is setting up a softphone integration (RingCentral, Zoom Phone, etc.) with screen pop and CTI features."],
      [/\/messaging\/channels/, "Channels page — the user can enable and configure communication channels: Web chat, SMS, IVR/Voice, WhatsApp, Facebook, Email, and more."],
      [/\/messaging\/integrations\/netsuite/, "NetSuite ERP Integration — the user is configuring the NetSuite connection (OAuth, AI skills, workflow AI node setup)."],
      [/\/messaging\/integrations\/hubspot/, "HubSpot CRM Integration — the user is configuring HubSpot (OAuth, contact lookup, AI skills, screen pop)."],
      [/\/messaging\/integrations\/shopify/, "Shopify Integration — the user is setting up the Shopify AI bot (OAuth, product search, order lookup, 35 AI skills)."],
      [/\/messaging\/integrations\/bigcommerce/, "BigCommerce Integration — the user is configuring the BigCommerce AI bot (OAuth, 33 AI skills)."],
      [/\/messaging\/integrations\/woocommerce/, "WooCommerce Integration — the user is setting up the WooCommerce AI bot (API key, 28 skills)."],
      [/\/messaging\/integrations\/magento/, "Magento / Adobe Commerce Integration — the user is configuring the Magento AI bot (API key, 24 skills, B2B support)."],
      [/\/messaging\/integrations\/square/, "Square Integration — the user is setting up Square Retail and/or Square Appointments AI features."],
      [/\/messaging\/integrations\/dynamics/, "Dynamics 365 Integration — the user is configuring the Microsoft Dynamics CRM/ERP connection."],
      [/\/messaging\/integrations\/salesforce/, "Salesforce Integration — the user is setting up the Salesforce CRM AI skills."],
      [/\/messaging\/integrations/, "Integrations page — the user can see and configure all available integrations (CRM, e-commerce, ERP, scheduling, etc.). Each integration card links to its settings."],
      [/\/messaging\/ai-configuration/, "AI Configuration — the user is setting up AI models, system prompts, knowledge base connections, and channel overrides for a bot."],
      [/\/messaging\/site-settings/, "Site Settings — global configuration for this Velaro site: AI defaults, routing, agent availability, escalation rules, and more."],
      [/\/messaging\/routing/, "Routing Rules — the user is configuring how incoming conversations are assigned to teams and agents based on conditions."],
      [/\/messaging\/teams/, "Teams — the user is managing agent groups, team schedules, and escalation tiers."],
      [/\/messaging\/deployments/, "Deployments — the user can create and configure chat widget deployments for their website."],
      [/\/messaging\/knowledge-base/, "Knowledge Base — the user is managing articles and documents that the AI bot uses to answer questions."],
      [/\/messaging\/surveys/, "Surveys / CSAT — the user is configuring post-chat satisfaction surveys."],
      [/\/messaging\/email-campaigns/, "Email Campaigns — the user can create and send outbound email broadcasts to contact lists."],
      [/\/messaging\/ivr-campaigns/, "IVR Outbound Campaigns — the user can configure automated outbound call campaigns."],
      [/\/reports\/value/, "Value Report — shows ROI, bot containment rate, cost savings, top skills used, and channel breakdown."],
      [/\/reports/, "Reports & Analytics — the user can view conversation metrics, agent performance, bot effectiveness, and channel data."],
      [/\/billing/, "Billing & Subscription — the user can view their plan, manage seats, and see invoice history."],
      [/\/contact-center/, "Contact Center — agent workspace overview showing live queue, availability, and conversation monitoring."],
      [/\/admin/, "Superadmin panel — Velaro-internal administration tools (site management, database, diagnostics)."],
      [/\/$|^$/, "Dashboard / Home — overview of site activity, recent conversations, and quick-access links."],
    ];
    for (const [pattern, label] of PAGE_LABELS) {
      if (pattern.test(p)) return label;
    }
    return `Page: ${pathname}`;
  };

  const { profile } = useContext(ProfileContext);
  const { status } = useContext(StatusContext);
  const { deployedAt } = useVersion();
  const { site } = useSite();

  const statusStr = status as string;

  const sidebarClass = toggleSidebar ? "w-64" : "w-14";
  const toggleSidebarIcon = () => setToggleSidebar((prev) => !prev);

  const expandMenu = (menuId: string) => {
    setExpandedMenus((prev) => {
      const index = prev.indexOf(menuId);
      return index > -1
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId];
    });
  };

  // Auto-expand the menu group that contains the current route
  useEffect(() => {
    const activeGroup = menuItems.find(
      (item) =>
        item.enabled &&
        item.sublinks?.some(
          (sub) =>
            sub.enabled &&
            (location.pathname === sub.to ||
              location.pathname.startsWith(sub.to + "/"))
        )
    );
    if (activeGroup && !expandedMenus.includes(activeGroup.label)) {
      setExpandedMenus((prev) => [...prev, activeGroup.label]);
    }
  }, [location.pathname, menuItems]);

  // Build breadcrumb from menu structure + current path
  const breadcrumb = useMemo(() => {
    const path = location.pathname;
    if (path === "/" || path === "") return [];
    const crumbs: { label: string; to?: string }[] = [];
    for (const item of menuItems) {
      if (!item.enabled) continue;
      if (item.sublinks) {
        const matches = item.sublinks.filter(
          (sub) =>
            sub.enabled &&
            (path === sub.to || path.startsWith(sub.to + "/"))
        );
        if (matches.length > 0) {
          // Pick most specific (longest path)
          const best = matches.sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0))[0];
          crumbs.push({ label: item.label });
          crumbs.push({ label: best.label, to: best.to });
          break;
        }
      } else if (
        item.to &&
        (path === item.to || path.startsWith(item.to + "/"))
      ) {
        crumbs.push({ label: item.label, to: item.to });
        break;
      }
    }
    return crumbs;
  }, [location.pathname, menuItems]);

  // Close mobile sidebar on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const renderMenu = (item: MenuItem, index: number) => {
    if (!item.enabled) return null;

    const onVisit = () => {
      setMobileOpen(false);
      if (!leftNavExpanded) setLeftNavExpanded(false);
    };

    if (!item.sublinks) {
      return (
        <MainLink
          key={index}
          label={item.label}
          to={item.to}
          icon={item.icon}
          onVisit={onVisit}
          showTooltip={toggleSidebar}
          toggleSidebar={toggleSidebar}
        />
      );
    }

    return (
      <ExpandableMenuLink
        key={index}
        onExpand={() => expandMenu(item.label)}
        label={toggleSidebar ? item.label : ""}
        icon={item.icon}
        expanded={expandedMenus.includes(item.label)}
        toggleSidebar={toggleSidebar}
      >
        {leftNavExpanded &&
          item.sublinks.map((sublink) =>
            sublink.enabled ? (
              !toggleSidebar ? (
                <SubLink
                  key={sublink.to}
                  to={sublink.to}
                  label={sublink.label}
                  onVisit={onVisit}
                  end={sublink.end}
                />
              ) : (
                <div key={sublink.to} className="border-l ml-5">
                  <SubLink
                    to={sublink.to}
                    label={sublink.label}
                    onVisit={onVisit}
                    end={sublink.end}
                  />
                </div>
              )
            ) : null
          )}
      </ExpandableMenuLink>
    );
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Mobile top bar — only visible on small screens */}
      <div className="md:hidden flex items-center justify-between px-3 py-2 v-nav z-50 shrink-0">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="v-nav-hover p-2 rounded text-white"
          aria-label="Open navigation"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <rect y="3" width="20" height="2" rx="1"/>
            <rect y="9" width="20" height="2" rx="1"/>
            <rect y="15" width="20" height="2" rx="1"/>
          </svg>
        </button>
        <img src={LogoWhite} alt="Logo" style={{ width: 36, height: 31 }} onClick={() => navigate("/")} className="cursor-pointer" />
        <div className="w-9" />
      </div>

      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="main-layout flex flex-1 overflow-hidden">
        <div
          className={`relative flex flex-col flex-none px-2 v-nav z-50
            md:static md:translate-x-0 md:flex
            fixed top-0 left-0 h-full transition-transform duration-200
            ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
            md:translate-x-0
            ${toggleSidebar ? "w-64" : "md:w-[52px] w-64"}
            ${leftNavExpanded ? "menu_active" : ""}`}
        >
          <div className="flex items-center justify-between pt-6 pb-5 md:flex hidden">
            <div className="flex flex-col items-start gap-1">
              <img
                src={LogoWhite}
                alt="Logo"
                className="flex-shrink-0 cursor-pointer"
                style={{ width: 44, height: 38, minWidth: 44, minHeight: 38 }}
                onClick={() => navigate("/")}
              />
            </div>
            <Tooltip
              text={toggleSidebar ? "Collapse" : "Expand"}
              direction="right"
              minWidth="70px"
              offset={toggleSidebar ? "15px" : "25px"}
            >
              <button
                onClick={toggleSidebarIcon}
                className={`v-nav-hover p-2 rounded ${
                  toggleSidebar
                    ? "text-white"
                    : "fill-black relative left-6 hover:bg-slate-gray-200"
                }`}
              >
                {toggleSidebar ? <IconCollapse /> : <IconExpand />}
              </button>
            </Tooltip>
          </div>
          {/* Mobile sidebar header — close button only, no logo (logo is in top bar) */}
          <div className="flex items-center justify-between pt-4 pb-3 md:hidden">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="v-nav-hover p-2 rounded text-white ml-auto"
              aria-label="Close navigation"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
              </svg>
            </button>
          </div>

          <div
            className="overflow-y-auto flex-1 overflow-x-hidden"
            style={{
              maxHeight: "calc(100vh - 4rem)",
              scrollbarColor: "#d1e4ff #3794ff",
              scrollbarWidth: "thin"
            }}
          >
            {menuItems.map(renderMenu)}
          </div>

          <div className="border-t v-nav-sep my-2">
            <div className="py-2">
              <MainLink
                to={messagingClientUrl}
                label="Agent Workspace"
                icon={<LinkExternal01 />}
                onVisit={() => {}}
                showTooltip={toggleSidebar}
                toggleSidebar={toggleSidebar}
                target="velaro-workspace"
              />
            </div>
            <div className="border-t v-nav-sep"></div>
            <div className="relative flex items-center">
              <div className="flex-1">
                <ProfileButton
                  picture={profile.picture}
                  status={statusStr}
                  onToggle={() => {
                    setShowProfileMenu((prev) => !prev);
                  }}
                  label={
                    profile.firstName && profile.lastName
                      ? `${profile.firstName} ${profile.lastName}`
                      : profile.displayName
                  }
                  toggleSidebar={toggleSidebar}
                />
              </div>
            </div>
            {showProfileMenu && (
              <DesktopProfileMenu
                onClose={() => setShowProfileMenu(false)}
                profile={profile}
              />
            )}
            {toggleSidebar && deployedAt && profile.email?.toLowerCase().endsWith("@velaro.com") && (
              <div className="px-3 pb-1 text-[10px] text-white/40 leading-tight">
                {new Date(deployedAt).toLocaleString("en-US", {
                  month: "short", day: "numeric",
                  hour: "2-digit", minute: "2-digit",
                  timeZone: "America/Los_Angeles", timeZoneName: "short"
                })}
              </div>
            )}
            <div className="border-t v-nav-sep"></div>
            {toggleSidebar && (
              <div className="flex items-center justify-center gap-2 py-1.5">
                <button
                  type="button"
                  onClick={decreaseFont}
                  disabled={fontStep === 0}
                  aria-label="Decrease text size"
                  title="Smaller text"
                  className="text-white/80 hover:text-white disabled:opacity-30 transition-colors select-none font-medium leading-none"
                  style={{ fontSize: 11 }}
                >a</button>
                <div className="flex gap-1 items-center">
                  {ZOOM_LEVELS.map((_, i) => {
                    const isMid = i === Math.floor(ZOOM_LEVELS.length / 2);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setFontStep(i)}
                        aria-label={isMid ? "Reset text size" : `Text size ${i + 1}`}
                        title={isMid ? "Reset to default" : undefined}
                        className={`rounded-full transition-all hover:opacity-100 ${
                          i === fontStep
                            ? "w-2 h-2 bg-white"
                            : isMid
                              ? "w-1.5 h-1.5 bg-white/50 hover:bg-white/80"
                              : "w-1 h-1 bg-white/40 hover:bg-white/70"
                        }`}
                      />
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={increaseFont}
                  disabled={fontStep === ZOOM_LEVELS.length - 1}
                  aria-label="Increase text size"
                  title="Larger text"
                  className="text-white/80 hover:text-white disabled:opacity-30 transition-colors select-none font-semibold leading-none"
                  style={{ fontSize: 17 }}
                >A</button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col flex-auto min-w-0 bg-slate-gray-50 dark:bg-slate-gray-900 dark:text-slate-gray-100 shrink overflow-hidden" style={zoomStyle}>
          {/* Breadcrumb — outside scroll container so content never bleeds above it */}
          {breadcrumb.length > 0 && (
            <nav className={`shrink-0 z-30 flex items-center gap-1 text-xs text-slate-400 bg-slate-gray-50 dark:bg-slate-gray-900 border-b border-slate-200/60 ${toggleSidebar ? "px-6 pt-3 pb-2" : "px-16 pt-3 pb-2"}`}>
              {breadcrumb.map((crumb, i) => {
                const isLast = i === breadcrumb.length - 1;
                return (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-slate-300 mx-0.5">›</span>}
                    {isLast || !crumb.to ? (
                      <span className={isLast ? "text-slate-600 font-medium" : "text-slate-400"}>
                        {crumb.label}
                      </span>
                    ) : (
                      <Link
                        to={crumb.to}
                        className="hover:text-primary transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
          )}

          <main
            className={`flex-auto overflow-auto ${
              toggleSidebar ? "p-3 sm:p-6" : "py-3 sm:py-6 pr-3 sm:pr-6 pl-16"
            } ${breadcrumb.length > 0 ? "pt-4" : ""}`}
          >
            <NewVersionBanner />
            {props.children}
          </main>
        </div>
      </div>

      {/* Moshky — global FAB, always available on every page */}
      {!showCopilot && (
        <button
          type="button"
          onMouseDown={(e) => {
            fabDragging.current = false;
            fabDragStart.current = { mx: e.clientX, my: e.clientY, ob: fabPos.bottom, or: fabPos.right };
            e.preventDefault();
          }}
          onClick={() => { if (!fabDragging.current) setShowCopilot(true); }}
          style={{ bottom: fabPos.bottom, right: fabPos.right }}
          className="fixed z-[9999] flex items-center justify-center bg-primary hover:bg-primary-active text-white rounded-2xl w-14 h-14 shadow-xl hover:shadow-2xl transition-all cursor-grab active:cursor-grabbing select-none hover:scale-105"
          aria-label="Open Moshky"
          title="Velaro AI — Moshky"
        >
          <svg width="26" height="22" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z" fill="white"/>
            <path d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z" fill="white"/>
            <path d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z" fill="white"/>
          </svg>
        </button>
      )}
      {showCopilot && (
        <CopilotAssistant
          context={`## Where the user is right now\n${getPageContext(location.pathname)}\n\nRaw path: ${location.pathname}\n\nAlways use [→ Label](/path) format when linking to any page in Velaro admin.`}
          integrationKey={integrationKey}
          onClose={() => setShowCopilot(false)}
        />
      )}
    </div>
  );
}
